import { useCallback, useRef, useState } from "react"
import { agentsApi } from "@/lib/api/agents"
import type { VehicleSide } from "@/lib/api/types"
import { createLimiter } from "./concurrency"
import { preflightImage } from "./preflight"
import { deriveQEVerdict } from "./photo-verdict"
import { basicSideOf, type QEPhoto } from "./types"
import type { QuickEstimateActions } from "./use-quick-estimate-state"

// Quick Estimate 3 — capture pipeline.
//
// Single hook the wizard mounts once. Both the photo step and the
// send step consume its outputs (inflight queue, retry capability,
// trigger function). Lifting this out of step-photos lets the send
// step's "Add more" button kick off the same preflight + concurrency
// + retry chain — without that, send-step adds would skip preflight
// or duplicate the machinery.
//
// File-reference lifecycle:
//   • addPhoto path → stored in filesRef while in-flight; deleted on
//     successful eval, kept on failure (so Retry can run).
//   • Retry path → re-uses the stored File. Capped at 3 retries
//     (after that, lightbox surfaces "Send without AI tag").
//   • Refresh / step transitions → filesRef survives across step
//     transitions inside the wizard but is wiped on a tab refresh
//     (it lives in component state, not sessionStorage).

interface InflightTile {
  id: string
  previewUrl: string
}

const uploadLimiter = createLimiter(3)

let nextId = 1
function makeId() {
  return `qe3-${Date.now()}-${nextId++}`
}

// Optional context applied to photos created by the next capture(s) —
// used by guided per-panel capture so each shot inherits the panel's
// declared side and is grouped under that panel.
export interface CaptureContext {
  side?: VehicleSide
  taggedPanelId?: string
}

export interface UseQECapture {
  fileInputRef: React.RefObject<HTMLInputElement | null>
  inflight: InflightTile[]
  retryingId: string | null
  // Returns true if we still hold a File ref for this photo (i.e. retry
  // is possible, subject to the retry-count cap).
  hasFileRef: (id: string) => boolean
  triggerCapture: () => void
  handleFile: (file: File) => Promise<void>
  handleRetry: (photo: QEPhoto) => Promise<void>
  // Drops the File ref for an id — call when the advisor removes the
  // photo so we don't leak.
  forgetFile: (id: string) => void
  // Set (or clear) the context applied to subsequent captures. Guided
  // capture sets this before each triggerCapture; free capture leaves it null.
  setCaptureContext: (ctx: CaptureContext | null) => void
}

export function useQECapture(actions: QuickEstimateActions): UseQECapture {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const filesRef = useRef<Map<string, File>>(new Map())
  const captureContextRef = useRef<CaptureContext | null>(null)
  const [inflight, setInflight] = useState<InflightTile[]>([])
  const [retryingId, setRetryingId] = useState<string | null>(null)

  const setCaptureContext = useCallback((ctx: CaptureContext | null) => {
    captureContextRef.current = ctx
  }, [])

  const triggerCapture = useCallback(() => {
    try {
      navigator.vibrate?.(10)
    } catch {
      // ignore
    }
    fileInputRef.current?.click()
  }, [])

  const evaluateAndPlace = useCallback(
    async (
      id: string,
      file: File,
      isRetry: boolean,
      ctx: CaptureContext | null,
    ) => {
      try {
        const result = await uploadLimiter.run(() =>
          agentsApi.evaluateImage(file),
        )
        const ev = result.evaluation
        // Normalize AI's 13-value side response down to one of 5
        // basics — the picker only ever exposes those, so the user
        // can't end up looking at a "Driver" tile that's actually
        // stored as FRONT_LEFT_SIDE. In guided capture, the advisor
        // already declared the side via the 3D tag, so that wins.
        const rawSide = (ev.side ?? "FRONT_CENTER") as VehicleSide
        const side = ctx?.side ?? basicSideOf(rawSide)
        const patch: Partial<QEPhoto> = {
          uploadedUrl: ev.imageUrl ?? result.upload?.url ?? "",
          detectedSide: side,
          qualityScore: ev.qualityScore ?? 0,
          qualified: !!ev.qualified,
          qualityIssues: ev.qualityIssues ?? [],
          verdict: deriveQEVerdict(ev),
          // A guided/declared side counts as an explicit override.
          sideOverridden: !!ctx?.side,
        }
        if (isRetry) {
          actions.updatePhoto(id, patch)
        } else {
          actions.addPhoto({
            id,
            previewUrl: URL.createObjectURL(file),
            uploadedUrl: patch.uploadedUrl ?? "",
            detectedSide: patch.detectedSide ?? "FRONT_CENTER",
            qualityScore: patch.qualityScore ?? 0,
            qualified: patch.qualified ?? false,
            qualityIssues: patch.qualityIssues ?? [],
            verdict: patch.verdict ?? "good",
            sideOverridden: patch.sideOverridden,
            retryCount: 0,
            takenAt: Date.now(),
            taggedPanelId: ctx?.taggedPanelId,
          })
        }
        // Success — drop the File ref; we don't need it anymore.
        filesRef.current.delete(id)
      } catch {
        if (isRetry) {
          // Increment retry count; keep File ref so Retry stays
          // available until the cap.
          actions.updatePhoto(id, {
            verdict: "eval-failed",
            uploadedUrl: "",
            qualityScore: 0,
            qualified: false,
            qualityIssues: [],
          })
        } else {
          actions.addPhoto({
            id,
            previewUrl: URL.createObjectURL(file),
            uploadedUrl: "",
            detectedSide: ctx?.side ?? "FRONT_CENTER",
            qualityScore: 0,
            qualified: false,
            qualityIssues: [],
            verdict: "eval-failed",
            sideOverridden: !!ctx?.side,
            retryCount: 0,
            takenAt: Date.now(),
            taggedPanelId: ctx?.taggedPanelId,
          })
        }
      }
    },
    [actions],
  )

  const handleFile = useCallback(
    async (rawFile: File) => {
      const id = makeId()
      // Snapshot the context now so rapid panel switches mid-eval don't
      // mis-attribute this photo.
      const ctx = captureContextRef.current
      const previewUrl = URL.createObjectURL(rawFile)
      setInflight((curr) => [...curr, { id, previewUrl }])

      const pf = await preflightImage(rawFile)
      if (!pf.ok) {
        actions.addPhoto({
          id,
          previewUrl,
          uploadedUrl: "",
          detectedSide: ctx?.side ?? "FRONT_CENTER",
          qualityScore: 0,
          qualified: false,
          qualityIssues: [pf.message],
          verdict: "low-quality",
          sideOverridden: !!ctx?.side,
          retryCount: 0,
          takenAt: Date.now(),
          description: pf.message,
          taggedPanelId: ctx?.taggedPanelId,
        })
        setInflight((curr) => curr.filter((p) => p.id !== id))
        return
      }
      filesRef.current.set(id, pf.file)
      await evaluateAndPlace(id, pf.file, /* isRetry */ false, ctx)
      setInflight((curr) => curr.filter((p) => p.id !== id))
    },
    [actions, evaluateAndPlace],
  )

  const handleRetry = useCallback(
    async (photo: QEPhoto) => {
      const file = filesRef.current.get(photo.id)
      if (!file) return
      if ((photo.retryCount ?? 0) >= 3) return
      setRetryingId(photo.id)
      // Bump retry count on the photo. We do this on the way IN so a
      // synchronous lightbox re-read sees it; the failure path below
      // doesn't need to bump again.
      actions.updatePhoto(photo.id, {
        retryCount: (photo.retryCount ?? 0) + 1,
      })
      // Retry keeps the photo's existing side/panel — pass null context.
      await evaluateAndPlace(photo.id, file, /* isRetry */ true, null)
      setRetryingId((curr) => (curr === photo.id ? null : curr))
    },
    [actions, evaluateAndPlace],
  )

  const hasFileRef = useCallback((id: string) => filesRef.current.has(id), [])

  const forgetFile = useCallback((id: string) => {
    filesRef.current.delete(id)
  }, [])

  return {
    fileInputRef,
    inflight,
    retryingId,
    hasFileRef,
    triggerCapture,
    handleFile,
    handleRetry,
    forgetFile,
    setCaptureContext,
  }
}
